import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Priority languages (human-reviewed)
import bg from './locales/bg.json';
import cs from './locales/cs.json';
import da from './locales/da.json';
import de from './locales/de.json';
import el from './locales/el.json';
import en from './locales/en.json';
import es from './locales/es.json';
import et from './locales/et.json';
import fi from './locales/fi.json';
import fr from './locales/fr.json';
import ga from './locales/ga.json';
import hr from './locales/hr.json';
import hu from './locales/hu.json';
import it from './locales/it.json';
import lt from './locales/lt.json';
import lv from './locales/lv.json';
import mt from './locales/mt.json';
import nl from './locales/nl.json';
import pl from './locales/pl.json';
import pt from './locales/pt.json';
import ro from './locales/ro.json';
import sk from './locales/sk.json';
import sl from './locales/sl.json';
import sv from './locales/sv.json';

const resources = {
  bg: { translation: bg },
  cs: { translation: cs },
  da: { translation: da },
  de: { translation: de },
  el: { translation: el },
  en: { translation: en },
  es: { translation: es },
  et: { translation: et },
  fi: { translation: fi },
  fr: { translation: fr },
  ga: { translation: ga },
  hr: { translation: hr },
  hu: { translation: hu },
  it: { translation: it },
  lt: { translation: lt },
  lv: { translation: lv },
  mt: { translation: mt },
  nl: { translation: nl },
  pl: { translation: pl },
  pt: { translation: pt },
  ro: { translation: ro },
  sk: { translation: sk },
  sl: { translation: sl },
  sv: { translation: sv },
};

// All supported language codes with native names (for language selector)
// status: 'primary' = human-authored 100% | 'beta' = AI-translated | 'verified' = native-reviewed
export const LANGUAGES = [
  // Primary — 100% coverage, human-authored + reviewed
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', status: 'primary' },
  { code: 'en', name: 'English', flag: '🇬🇧', status: 'primary' },
  // Priority Beta — AI-translated with glossary alignment
  { code: 'fr', name: 'Français', flag: '🇫🇷', status: 'beta' },
  { code: 'es', name: 'Español', flag: '🇪🇸', status: 'beta' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', status: 'beta' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱', status: 'beta' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱', status: 'beta' },
  { code: 'pt', name: 'Português', flag: '🇵🇹', status: 'beta' },
  // Hidden — skeleton only (~5% coverage), not shown in LanguageSelector until translated
  { code: 'bg', name: 'Български', flag: '🇧🇬', status: 'hidden' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿', status: 'hidden' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰', status: 'hidden' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷', status: 'hidden' },
  { code: 'et', name: 'Eesti', flag: '🇪🇪', status: 'hidden' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮', status: 'hidden' },
  { code: 'ga', name: 'Gaeilge', flag: '🇮🇪', status: 'hidden' },
  { code: 'hr', name: 'Hrvatski', flag: '🇭🇷', status: 'hidden' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺', status: 'hidden' },
  { code: 'lt', name: 'Lietuvių', flag: '🇱🇹', status: 'hidden' },
  { code: 'lv', name: 'Latviešu', flag: '🇱🇻', status: 'hidden' },
  { code: 'mt', name: 'Malti', flag: '🇲🇹', status: 'hidden' },
  { code: 'ro', name: 'Română', flag: '🇷🇴', status: 'hidden' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰', status: 'hidden' },
  { code: 'sl', name: 'Slovenščina', flag: '🇸🇮', status: 'hidden' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪', status: 'hidden' },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: ['en', 'de'],
    supportedLngs: Object.keys(resources),
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'aif_language',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
  });

// Sync HTML lang attribute with i18next language (a11y + SEO)
// Screen readers use document.documentElement.lang to select pronunciation
function syncHtmlLang(lng) {
  if (typeof document !== 'undefined' && lng) {
    document.documentElement.lang = lng;
  }
}

i18n.on('initialized', () => {
  syncHtmlLang(i18n.language);
});

i18n.on('languageChanged', (lng) => {
  syncHtmlLang(lng);
});

export default i18n;
